import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SelectLocation from '../../../components/SelectLocation/SelectLocation';
import Header from '../../../components/header/Header';
import useLocalizedMoney from '../../../hooks/useLocalizedMoney';
import { calcTotalPrice, getValueByLanguage, showNotificationDialog } from '../../../utils';
import styles from './styles.module.css';
import { localStorageKeys, PRICE_TYPE } from '../../../utils/Constants';
import useR05Api from '../../../api/useR05Api';
import i18n from '../../../assets/languages/configs';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import CustomAntButton from '../../../components/CustomAntButton/CustomAntButton';
import { useReservationFlow } from '../../../hooks/useReservationFlow';
import { MultilingualType, Stops, TicketInfo } from 'types';

const ReservationSectionScreen = () => {
    const { currentIndex, next, back, selectNumber, ticketInfo, isEdit, reservationCombo, localReservation } =
        useReservationFlow();

    const reservationTicket = useMemo(() => {
        return reservationCombo[currentIndex];
    }, [currentIndex, reservationCombo]);

    const [originStop, setOriginStop] = useState<string | null>(() => {
        return reservationTicket?.reservationInfo?.originStop?.value ?? null;
    });
    const [destStop, setDestStop] = useState<string | null>(() => {
        return reservationTicket?.reservationInfo?.destStop?.value ?? null;
    });
    const [stopList, setStopList] = useState<Stops>([]);
    const [isLoading, setIsLoading] = useState(false);

    const { t } = useTranslation();
    const { formatMoney, currencyUnit } = useLocalizedMoney();
    const { fetchStopList } = useR05Api();

    const { language } = i18n;
    const l = useCallback((text?: MultilingualType | null) => getValueByLanguage(text, language), [language]);

    useEffect(() => {
        void (async () => {
            try {
                setIsLoading(true);
                const stockId = reservationTicket.reservationInfo!.stockId;
                if (stockId == null) {
                    setIsLoading(false);
                    setStopList([]);
                    return;
                }
                const stops = (await fetchStopList({ stockId })) ?? [];
                setIsLoading(false);
                setStopList(stops);
            } catch {
                setIsLoading(false);
            }
        })();
    }, [reservationTicket?.reservationInfo?.stockId, fetchStopList]);

    const originStopList = useMemo(() => {
        const uniqueOriginStops: Record<string, MultilingualType> = {};
        stopList.forEach((stop) => {
            uniqueOriginStops[stop.originStopId] = stop.originStopName;
        });
        const originStops = Object.entries(uniqueOriginStops).map(([originStopId, shortname]) => ({
            label: l(shortname),
            value: originStopId,
            shortName: shortname,
        }));
        return originStops;
    }, [stopList, l]);

    const destStopList = useMemo(() => {
        const uniqueDestStops: Record<string, MultilingualType> = {};
        stopList.forEach((stop) => {
            uniqueDestStops[stop.destStopId] = stop.destStopName;
        });
        const destStops = Object.entries(uniqueDestStops).map(([destStopId, shortname]) => ({
            label: l(shortname),
            value: destStopId,
            shortName: shortname,
            disabled: !stopList.some((stop) => {
                if (!originStop) {
                    return true;
                }
                return (
                    String(stop.destStopId) === String(destStopId) && String(stop.originStopId) === String(originStop)
                );
            }),
        }));

        return destStops;
    }, [stopList, originStop, l]);

    const { stopPriceAdult, stopPriceCustom1, stopPriceCustom2, stopPriceCustom3 } = useMemo(() => {
        const stop = stopList.find(
            (stop) => String(stop.originStopId) === String(originStop) && String(stop.destStopId) === String(destStop),
        );

        return {
            stopPriceAdult: stop?.priceAdult ?? 0,
            stopPriceCustom1: stop?.priceCustom1 ?? 0,
            stopPriceCustom2: stop?.priceCustom2 ?? 0,
            stopPriceCustom3: stop?.priceCustom3 ?? 0,
        };
    }, [originStop, destStop, stopList]);

    // const totalPrice = useMemo(() => {
    //     let totalPriceAdult = 0;
    //     let totalPriceCustom1 = 0;
    //     let totalPriceCustom2 = 0;
    //     let totalPriceCustom3 = 0;

    //     if (ticketInfo?.isCombination) {
    //         ticketInfo?.comboItems.forEach((item) => {
    //             if (item.isReserve) {
    //                 totalPriceAdult += stopPriceAdult || 0;
    //                 totalPriceCustom1 += stopPriceCustom1 || 0;
    //                 totalPriceCustom2 += stopPriceCustom2 || 0;
    //                 totalPriceCustom3 += stopPriceCustom3 || 0;
    //             }
    //         });
    //     } else {
    //         totalPriceAdult = stopPriceAdult || 0;
    //         totalPriceCustom1 = stopPriceCustom1 || 0;
    //         totalPriceCustom2 = stopPriceCustom2 || 0;
    //         totalPriceCustom3 = stopPriceCustom3 || 0;
    //     }

    //     return {
    //         priceAdult: totalPriceAdult,
    //         priceCustom1: totalPriceCustom1,
    //         priceCustom2: totalPriceCustom2,
    //         priceCustom3: totalPriceCustom3,
    //     };
    // }, [ticketInfo, stopPriceAdult, stopPriceCustom1, stopPriceCustom2, stopPriceCustom3]);

    const handleOriginStopChange = (value: string) => {
        setOriginStop(value);
        if (!destStop) {
            return;
        }
        const hasRoute = stopList.some(
            (stop) => String(stop.destStopId) === String(destStop) && String(stop.originStopId) === String(value),
        );
        if (!hasRoute) {
            setDestStop(null);
        }
    };
    const handleDestStopChange = (value: string) => {
        setDestStop(value);
    };
    const handleNextStep = () => {
        if (!originStop || !destStop) {
            showNotificationDialog('error', 'selectNumberError', t('notification.title'), t('validate.required-stops'));
            return;
        }

        const reservationInfoData = {
            ...localReservation,
            reservationCombo: reservationCombo.map((item: TicketInfo) =>
                item.displayPriority === reservationTicket.displayPriority
                    ? {
                          ...item,
                          reservationInfo: {
                              ...item?.reservationInfo,
                              originStop: originStopList.find((item) => item.value === originStop),
                              destStop: destStopList.find((item) => item.value === destStop),
                              priceAdult: stopPriceAdult,
                              priceCustom1: stopPriceCustom1,
                              priceCustom2: stopPriceCustom2,
                              priceCustom3: stopPriceCustom3,
                          },
                      }
                    : item,
            ),
        };
        localStorage.setItem(localStorageKeys.RESERVATION_INFO_DATA, JSON.stringify(reservationInfoData));

        next('section');
    };

    return (
        <div className="content-container scrollable">
            <Header title={!isEdit ? t('header.buy-ticket') : t('reservation.header')} callback={back} />
            {isLoading && <CenterLoading />}
            <div className={styles.mainWrapper}>
                <div className={styles.locationPicker}>
                    <SelectLocation
                        onChangeOriginStop={handleOriginStopChange}
                        onChangeDestStop={handleDestStopChange}
                        originStopList={originStopList}
                        destStopList={destStopList}
                        originStop={stopList.length > 0 ? originStop : null}
                        destStop={stopList.length > 0 ? destStop : null}
                    />
                </div>
                {originStop && destStop && stopList.length > 0 && ticketInfo.priceType === PRICE_TYPE.ADDITION && (
                    <div className={styles.totalPrice}>
                        <div>
                            {(selectNumber?.adult ?? 0) > 0 && (
                                <div>
                                    <span>{t('ticket-detail.adult')}</span> /{' '}
                                    <span>{formatMoney(stopPriceAdult, 'JPY')}</span> x{' '}
                                    <span>{selectNumber.adult}</span>
                                </div>
                            )}
                            {(selectNumber?.custom1 ?? 0) > 0 && (
                                <div>
                                    <span>{l(ticketInfo.custom1Name)}</span> /{' '}
                                    <span>{formatMoney(stopPriceCustom1, 'JPY')}</span> x{' '}
                                    <span>{selectNumber.custom1}</span>
                                </div>
                            )}
                            {(selectNumber?.custom2 ?? 0) > 0 && (
                                <div>
                                    <span>{l(ticketInfo.custom2Name)}</span> /{' '}
                                    <span>{formatMoney(stopPriceCustom2, 'JPY')}</span> x{' '}
                                    <span>{selectNumber.custom2}</span>
                                </div>
                            )}
                            {(selectNumber?.custom3 ?? 0) > 0 && (
                                <div>
                                    <span>{l(ticketInfo.custom3Name)}</span> /{' '}
                                    <span>{formatMoney(stopPriceCustom3, 'JPY')}</span> x{' '}
                                    <span>{selectNumber.custom3}</span>
                                </div>
                            )}
                        </div>
                        <div className={styles.price}>
                            {t('ticket-detail.subtotal-amount')}
                            <span className={styles.priceAmount}>
                                {formatMoney(
                                    calcTotalPrice(selectNumber, {
                                        adult: stopPriceAdult ?? 0,
                                        custom1: stopPriceCustom1 ?? 0,
                                        custom2: stopPriceCustom2 ?? 0,
                                        custom3: stopPriceCustom3 ?? 0,
                                    }),
                                )}
                            </span>
                            {currencyUnit('JPY')}
                            <span className={styles.taxIncluded}>{t('ticket-detail.tax-included')}</span>
                        </div>
                    </div>
                )}
                <div className={styles.btnArea}>
                    <CustomAntButton type="outlined" className={styles.coloredBtn} block onClick={handleNextStep}>
                        {t('reservation.next-step')}
                    </CustomAntButton>
                </div>
            </div>
        </div>
    );
};

export default ReservationSectionScreen;
